import { FC, useMemo, useState } from 'react';
import { Box, FormLabel, Popover, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import styles from './GuestsSelect.module.scss';
import { TrevelersIcon } from '../../../../../components/Icons/TrevelersIcon';

type GuestsSelectProps = {
  guests: number;
  onGuestsChange: (n: number) => void;
  invalid?: boolean;
  compactForMobile?: boolean;
};

const GuestsSelect: FC<GuestsSelectProps> = ({
  guests,
  onGuestsChange,
  invalid = false,
  compactForMobile = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const summary = useMemo(() => {
    return guests === 1 ? '1 guest' : `${guests} guests`;
  }, [guests]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const close = () => setAnchorEl(null);

  return (
    <Box
      className={styles.root}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.label}>
        <Typography
          variant="h11Semibold"
          color="#222B3A"
        >
          Guests
        </Typography>
      </FormLabel>

      <Box
        role="button"
        tabIndex={0}
        className={`${styles.trigger}${invalid ? ` ${styles.triggerInvalid}` : ''}`}
        onClick={handleOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <TrevelersIcon className={styles.triggerIcon} />
        <Typography
          variant="h10"
          className={styles.triggerText}
          noWrap
        >
          {summary}
        </Typography>
        <ExpandMoreIcon className={styles.chevron} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: styles.paper,
          },
        }}
        disableRestoreFocus
      >
        <Box className={styles.popoverInner}>
          <Box className={styles.row}>
            <Typography
              variant="h10Semibold"
              className={styles.rowLabel}
            >
              Guests
            </Typography>
            <Box className={styles.counter}>
              <button
                type="button"
                className={styles.counterBtn}
                onClick={() => onGuestsChange(Math.max(1, guests - 1))}
                disabled={guests <= 1}
                aria-label="Decrease guests"
              >
                −
              </button>
              <Box className={styles.counterValue}>{guests}</Box>
              <button
                type="button"
                className={styles.counterBtn}
                onClick={() => onGuestsChange(guests + 1)}
                aria-label="Increase guests"
              >
                +
              </button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default GuestsSelect;
